import * as React from 'react';
import { connect } from 'react-redux';

import { navigate } from 'libs/router';

import Page from 'components/page/Page';
import Spinner from 'components/spinner/Spinner';
import Pagination from 'components/pagination/Pagination';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import ProposalTile from '../../components/proposal-tile-v2/ProposalTile';
import ProposalTileFilter from '../../components/proposal-tiles-filter/ProposalTileFilter';
import ProposalNoResults from '../../components/no-results/ProposalNoResults';

import { success } from 'redux-store/reducers/common-reducer';
import { loadCreatorList, resetList, updateApprovedStatus, updateCreator } from '../../reducers/shared-access-list';
import { loadCreatorListInfo, resetInfo } from '../../reducers/shared-access-list-info';

import { __t, applyTranslations } from 'utils/language';
import {
  DEFAULT_LIST_CHANNELS,
  DEFAULT_MEMBER_CUSTOM_CHANNELS,
  getGroupedMembers,
  getNewSort
} from 'modules/proposal/utils/creator-proposal-utils';

import type {
  LocationWindow,
  PaginationLinks,
  PaginationMeta,
  ProposalListInfo,
  ProposalListMember,
  ProposalListMemberStatus,
  ProposalPageFilterOption,
  ProposalPageSortOption
} from 'types';
import type { ProposalTileChannelType, ProposalTileType } from 'modules/proposal/types/ProposalTile.type';

import './proposal-approve-page.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./proposal-approve-page.i18n.json'));

type StateProps = {
  listInfo: ProposalListInfo | undefined,
  creatorList: Array<ProposalListMember>,
  location: LocationWindow,
  paginationMeta: PaginationMeta,
  paginationLinks: PaginationLinks,
  listLoadingStatus: boolean,
  infoLoadingStatus: boolean
};

type DispatchProps = {
  dispatchLoadCreatorList: (page: number, filter: ProposalPageFilterOption, sort: ProposalPageSortOption) => void,
  dispatchUpdateCreator: (member: ProposalListMember, isApprovalStatusReset?: boolean) => Promise<void>,
  dispatchSetApprovalStatus: (a: ProposalListMember) => void,
  dispatchNoteSaved: () => void,
  dispatchLoadCreatorListInfo: () => Promise<ProposalListInfo>,
  dispatchResetCreatorList: () => void,
  dispatchResetCreatorListInfo: () => void
};

type Props = StateProps & DispatchProps;

type State = {
  currentSort: ProposalPageSortOption,
  currentFilter: ProposalPageFilterOption
};

class ProposalApprovePage extends React.Component<Props, State> {
  state: State = {
    currentFilter: 'all',
    currentSort: 'ig_desc'
  };

  componentDidMount() {
    const {
      dispatchResetCreatorList,
      dispatchResetCreatorListInfo,
      dispatchLoadCreatorListInfo
    } = this.props;

    dispatchResetCreatorList();
    dispatchResetCreatorListInfo();

    dispatchLoadCreatorListInfo()
      .then((listInfo) => this.setInitialSortAndFetch(listInfo));
  }

  private setInitialSortAndFetch(newListInfo: ProposalListInfo): void {
    const { location } = this.props;

    const query = location.query || {};
    const pageToOpen = query.page || 1;
    const sortToUse = query.sort || 'ig_desc';
    const filterToUse = query.filter || 'all';

    const newSort = getNewSort(newListInfo?.channels ?? DEFAULT_LIST_CHANNELS, sortToUse);

    this.setState({
      currentFilter: filterToUse,
      currentSort: newSort
    }, () => {
      this.loadListPage(pageToOpen);
    });
  }

  private loadListPage = (page: number = 1) => {
    const { dispatchLoadCreatorList, location } = this.props;
    const { currentSort, currentFilter } = this.state;
    dispatchLoadCreatorList(page, currentFilter, currentSort);

    const query = location.query || {};
    const token = query.token;

    setTimeout(() => {
      // oh..
      navigate.replace(`/shared-access/creator-approval?token=${token}&page=${page}&filter=${currentFilter}&sort=${currentSort}`);
    }, 100);
  };

  private handleFilterReset = () => {
    this.setState({
      currentSort: 'ig_desc',
      currentFilter: 'all'
    }, () => {
      this.loadListPage(1);
    });
  };

  private handleFilterChange = (newFilter: ProposalPageFilterOption) => {
    this.setState({
      currentFilter: newFilter
    }, () => {
      this.loadListPage(1);
    });
  };

  private handleSortChange = (newSort: ProposalPageSortOption) => {
    this.props.dispatchResetCreatorList();

    this.setState({
      currentSort: newSort
    }, () => {
      this.loadListPage(1);
    });
  };

  private async handleSetStatus(entry: ProposalListMember, status: ProposalListMemberStatus) {
    const { dispatchSetApprovalStatus, dispatchUpdateCreator } = this.props;

    const updatedEntity = {
      ...entry,
      approvalStatus: status
    };

    if (status === 'pending') {
      // clean favorite flag on status reset
      updatedEntity.isPreferred = false;
      await dispatchUpdateCreator(updatedEntity, true);
    }

    dispatchSetApprovalStatus(updatedEntity);
  }

  private handleToggleFavourite(entry: ProposalListMember) {
    const { dispatchUpdateCreator } = this.props;
    dispatchUpdateCreator({
      ...entry,
      isPreferred: !entry.isPreferred
    });
  }

  private handleNoteUpdate(entry: ProposalListMember, note?: string | null) {
    const { dispatchUpdateCreator, dispatchNoteSaved } = this.props;
    dispatchUpdateCreator({
      ...entry,
      brandRepresentativeNote: note
    }).then(dispatchNoteSaved);
  }

  private canHaveCustomChannels(): boolean {
    return this.props.listInfo?.channels.includes('custom') ?? false;
  }

  private getEntryCustomChannels(entry: ProposalListMember): Array<ProposalTileChannelType> | undefined {
    return this.canHaveCustomChannels()
      ? entry.selectedChannels ?? DEFAULT_MEMBER_CUSTOM_CHANNELS
      : undefined;
  }

  private renderEntry(entry: ProposalListMember) {
    const { listInfo } = this.props;
    const creatorProfile = entry.creatorProfile;
    const canResetStatus = entry.approvalStatus !== 'pending';
    const tileCustomChannels = this.getEntryCustomChannels(entry);

    return (
      <React.Fragment key={entry.id}>
        <ProposalTile
          isForBrand
          creatorProfile={creatorProfile}
          status={entry.approvalStatus}
          isFavorite={entry.isPreferred}
          posts={entry.images.instagram || []}
          postsTikTok={entry.images.tiktok || []}
          template={(listInfo?.templateType as ProposalTileType) ?? 'basic'}
          onApprove={() => { this.handleSetStatus(entry, 'approved'); }}
          onReject={() => { this.handleSetStatus(entry, 'declined'); }}
          onStatusReset={(canResetStatus) ? () => { this.handleSetStatus(entry, 'pending'); } : undefined}
          onToggleFavorite={() => this.handleToggleFavourite(entry)}
          notes={{
            brandRepresentative: entry.brandRepresentativeNote,
            accountManager: entry.accountManagerNote
          }}
          onNoteUpdate={(note) => this.handleNoteUpdate(entry, note)}
          customChannels={tileCustomChannels}
          channelOptions={listInfo?.channels ?? DEFAULT_LIST_CHANNELS}
        />
        <VerticalSpacing type="large" />
      </React.Fragment>
    );
  }

  render() {
    const {
      location,
      listInfo,
      creatorList,
      paginationMeta,
      paginationLinks,
      listLoadingStatus,
      infoLoadingStatus
    } = this.props;

    const { currentFilter, currentSort } = this.state;

    const groupedListData = getGroupedMembers(creatorList, currentSort);
    const isShowingHeadlines = listInfo?.isShowHeadlines ?? true;
    const hasNoResults = !listLoadingStatus
      && !infoLoadingStatus
      && listInfo
      && groupedListData.length === 0;

    return (
      <Page
        documentTitle={__t('shared_access_proposal_approve_page_title')}
      >
        {(listLoadingStatus || infoLoadingStatus) && <Spinner />}
        <div className="proposal-approve-page__header">
          <div className="proposal-approve-page__collection-name">
            {listInfo?.creatorListName ?? ''}
          </div>
          <VerticalSpacing type="large" />
          <div className="proposal-approve-page__title">
            {__t('proposal_approve_page.title')}
          </div>
          <div className="proposal-approve-page__subtitle">
            {__t('proposal_approve_page.subtitle')}
          </div>
        </div>
        <div className="proposal-approve-page__content">
          <div className="proposal-approve-page__filter">
            <ProposalTileFilter
              currentChannels={listInfo?.channels || []}
              isBrandPage
              sort={currentSort}
              filter={currentFilter}
              onFilterChange={this.handleFilterChange}
              onSortChange={this.handleSortChange}
            />
          </div>
          <VerticalSpacing type="medium" />
          {hasNoResults && (
            <ProposalNoResults
              canReset={currentFilter !== 'all'}
              onReset={this.handleFilterReset}
            />
          )}
          {!isShowingHeadlines && ( // used to add padding for filters
            <div className="proposal-create-page__tier-label">{'\u00a0'}</div>
          )}
          {
            groupedListData
              .map((entry, index) => {
                const entries = entry.data.map(this.renderEntry, this);
                return (
                  <React.Fragment key={`group-${entry.tier}-${index}`}>
                    {isShowingHeadlines && (
                      <div className="proposal-approve-page__tier-label">
                        {__t(`proposal_approve_page.tiers.${entry.tier}`)}
                      </div>
                    )}
                    {entries}
                  </React.Fragment>
                );
              })
          }
        </div>
        <Pagination
          location={location}
          meta={paginationMeta}
          links={paginationLinks}
          onClick={this.loadListPage}
        />
      </Page>
    );
  }
}

const mapStateToProps = ({ proposalApprovePage, proposalApprovePageInfo, router }): StateProps => ({
  listInfo: proposalApprovePageInfo.info,
  creatorList: proposalApprovePage.list,
  location: router.location,
  paginationMeta: proposalApprovePage.paginationMeta,
  paginationLinks: proposalApprovePage.paginationLinks,
  listLoadingStatus: proposalApprovePage.loadingStatus,
  infoLoadingStatus: proposalApprovePageInfo.loadingStatus
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchLoadCreatorListInfo: (): Promise<ProposalListInfo> => dispatch(loadCreatorListInfo()),
  dispatchLoadCreatorList: (page: number, filter: ProposalPageFilterOption, sort: ProposalPageSortOption): void =>
    dispatch(loadCreatorList(page, filter, sort)),
  dispatchUpdateCreator: (data: ProposalListMember, isApprovalStatusReset?: boolean): Promise<void> => (
    dispatch(updateCreator(data, isApprovalStatusReset))
  ),
  dispatchSetApprovalStatus: (data: ProposalListMember): void => dispatch(updateApprovedStatus(data)),
  dispatchNoteSaved: (): void => dispatch(success(__t('proposal_approve_page.note_saved_popup'), { hideDuration: 2000 })),
  dispatchResetCreatorList: (): void => dispatch(resetList()),
  dispatchResetCreatorListInfo: (): void => dispatch(resetInfo())
});

export default connect(mapStateToProps, mapDispatchToProps)(ProposalApprovePage);
